/**
 * ENG-2321 ULCM Feedback — central map of ULCM contact API error_codes
 * to user-facing messages.
 *
 * Keeps "Unknown error." from ever reaching the UI as a raw fallback.
 * Every ULCM propose / confirm / set-primary flow should use
 * `messageForContactError` so phrasing stays consistent across Add Email,
 * Add Phone, Make Primary, Compliance Propose, etc.
 *
 * Rules of engagement:
 * - Keys are the Thor `error_code` strings on 4xx responses from
 *   /api/v2/user/contacts/* and /api/v2/user/compliance/users/<id>/contacts/*.
 * - Values are plain sentences that can be dropped into an MUI Alert.
 * - If the caller has a more specific `detail` from the backend, prefer
 *   that; use this map only when `detail` is absent or generic.
 * - Fallback is a non-technical "please try again" — NEVER "Unknown error."
 */

export type ContactErrorCode =
  | 'contact_locked'
  | 'contact_already_claimed'
  | 'contact_phone_conflict'
  | 'unverified_contact'
  | 'not_verified'
  | 'not_found'
  | 'invalid_number'
  | 'not_sms_capable'
  | 'no_contact_for_preference'
  | 'rate_limited'
  | 'sms_capable_retry';

/**
 * Friendly copy for a phone number that is already owned by another account.
 * A phone number is an SMS-login identity, so it may belong to only one user.
 * Used both as the mapped message for `contact_phone_conflict` and as the
 * generic 409 fallback on the household-member save screens.
 */
export const PHONE_CONFLICT_MESSAGE =
  'This phone number is already linked to another account. A phone number can only be used on one account — try a different number, or remove it from the other account first.';

export const CONTACT_ERROR_MESSAGES: Record<ContactErrorCode, string> = {
  contact_locked:
    'This contact is locked. Please contact compliance to reactivate it.',
  contact_already_claimed:
    'This contact is already associated with another account. Try a different one, or sign out and sign in using those credentials.',
  contact_phone_conflict: PHONE_CONFLICT_MESSAGE,
  unverified_contact:
    'No verified contact on file. Verify an email or phone first so we can deliver this message.',
  not_verified:
    'This contact has not been verified yet. Confirm the verification code before using it.',
  not_found: 'We could not find that contact. Refresh and try again.',
  invalid_number:
    'That phone number could not be verified. Double-check the digits and try again.',
  not_sms_capable:
    'That phone line cannot receive SMS. Please use a mobile number that can receive text messages.',
  no_contact_for_preference:
    'You need at least one verified contact of that type before setting it as your preferred method.',
  rate_limited: 'Too many attempts. Please wait a bit and try again.',
  sms_capable_retry:
    'That phone can now receive SMS. Resubmit without the skip-SMS option so we can send a code.',
};

export const CONTACT_GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * Pick the best user-facing message for a ULCM contact error.
 *
 * Priority:
 *   1. `detail` from the server (unless it's the literal "Unknown error.").
 *   2. Lookup by `error_code` in the map above.
 *   3. Generic fallback.
 */
export const messageForContactError = (payload?: {
  error_code?: string | null;
  detail?: string | null;
}): string => {
  const detail = (payload?.detail ?? '').trim();
  if (detail && detail !== 'Unknown error.' && detail !== 'Unknown error') {
    return detail;
  }
  const code = payload?.error_code ?? null;
  if (code && code in CONTACT_ERROR_MESSAGES) {
    return CONTACT_ERROR_MESSAGES[code as ContactErrorCode];
  }
  return CONTACT_GENERIC_ERROR;
};

/**
 * Best user-facing message for a household-member save failure.
 *
 * Precedence:
 *   1. A mapped `error_code` (most specific).
 *   2. A specific server `detail`.
 *   3. HTTP 409 (contact already on another account) → the friendly
 *      "already linked" copy — so a conflict never reads as a generic retry.
 *   4. The caller-supplied generic fallback.
 */
export const messageForHouseholdMemberError = (
  payload?: {
    error_code?: string | null;
    detail?: string | null;
    status?: number | null;
  },
  fallback: string = CONTACT_GENERIC_ERROR
): string => {
  const code = payload?.error_code ?? null;
  if (code && code in CONTACT_ERROR_MESSAGES) {
    return CONTACT_ERROR_MESSAGES[code as ContactErrorCode];
  }
  const detail = (payload?.detail ?? '').trim();
  if (detail && detail !== 'Unknown error.' && detail !== 'Unknown error') {
    return detail;
  }
  if (payload?.status === 409) {
    return PHONE_CONFLICT_MESSAGE;
  }
  return fallback;
};

/**
 * Extract a field-keyed validation message from a Thor 400 payload.
 *
 * Thor field errors are DRF-style, e.g. `{ "email": ["Enter a valid email
 * address."] }`. The shared axios interceptor flattens `response.data` onto
 * the rejected error, so callers pass the caught error itself; a nested
 * `response.data` shape is still accepted for safety.
 */
export const fieldErrorFromPayload = (
  payload: unknown,
  field: string
): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const response = record.response;
  const responseData =
    response && typeof response === 'object'
      ? ((response as Record<string, unknown>).data as
          | Record<string, unknown>
          | undefined)
      : undefined;
  const sources = [record, responseData].filter(
    (source): source is Record<string, unknown> =>
      !!source && typeof source === 'object'
  );
  for (const source of sources) {
    const value = source[field];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (Array.isArray(value)) {
      const first = value.find(
        (entry) => typeof entry === 'string' && entry.trim()
      );
      if (typeof first === 'string') {
        return first.trim();
      }
    }
  }
  return null;
};
